<template>
    <container>
        <hox-loading-layer v-if="isLoading && isInitialLoad" />

        <AddFontModal
            v-if="addFontModalIsVisible && client?.mastheadUploadConfig"
            :upload-config="client.mastheadUploadConfig"
            @fontAdded="getFonts"
            @close="toggleAddFontModalIsVisible"
        />

        <div v-if="isLoading && !isInitialLoad" class="fonts-dashboard__loading-wrapper">
            <hox-loading-layer :is-full-screen="false" />
        </div>

        <div v-else-if="hasFonts && client" class="fonts">
            <div class="fonts__action-items">
                <SortFonts v-model="sortFontsBy" class="fonts__action-item" />

                <Button
                    v-if="userCanManageFonts"
                    class="fonts__action-item"
                    size="large"
                    type="primary"
                    data-testid="add-mew-font__button"
                    @click="toggleAddFontModalIsVisible"
                >
                    Add new font
                </Button>
            </div>

            <Row :gutter="15">
                <Col v-for="font of fonts" :key="font._id" :xs="24" :sm="12" :md="8" :lg="6">
                    <FontCard
                        :font="font"
                        :client-id="clientId"
                        :upload-config="client.mastheadUploadConfig"
                        @edit="getFonts"
                        @deleted="getFonts"
                    />
                </Col>
            </Row>
        </div>

        <div v-else class="fonts-dashboard__empty-message-wrapper">
            <template v-if="userCanManageFonts">
                <hox-empty-message>
                    <template #title>No custom fonts exist for this client</template>
                    <template #actionItems>
                        <Button
                            v-if="userCanManageFonts"
                            data-testid="new-font__button"
                            type="primary"
                            @click="toggleAddFontModalIsVisible"
                        >
                            Add new font
                        </Button>
                    </template>
                </hox-empty-message>
            </template>
            <template v-else>
                <hox-empty-message>
                    <template #title>You don't have access to any custom fonts for this client</template>
                </hox-empty-message>
            </template>
        </div>
    </container>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";

import type { Font } from "shared-types";
import { USER_ACTIONS } from "shared-utils/enums/user";

import fontsQuery from "@/apollo/queries/FontsByClient.gql";
import clientUploadConfigQuery from "@/apollo/queries/ClientUploadConfig.gql";
import FontCard from "@/components/FontCard.vue";
import Container from "@/components/Container.vue";
import AddFontModal from "@/components/AddFontModal.vue";
import SortFonts, { defaultFontsSortBy } from "@/components/SortFonts.vue";
import { apolloClient } from "@/apollo";
import { userCan, hasScope } from "@/services/Auth";
import type { MastheadUploadConfig } from "@/types/types";

const route = useRoute();

const fonts = ref<Font[]>([]);
const addFontModalIsVisible = ref(false);
const sortFontsBy = defineModel({ type: String, default: defaultFontsSortBy });
const isInitialLoad = ref(true);
const client = ref<{ _id: string; mastheadUploadConfig: MastheadUploadConfig } | null>(null);

const clientId = computed(() => route.params.clientId as string);
const hasFonts = computed(() => fonts.value.length > 0);

const isLoading = ref(false);
const userHasScope = computed(() => hasScope({ clientId: clientId.value }, false));
const userCanManageFonts = computed(() => userCan(USER_ACTIONS.canManageFonts, { clientId: clientId.value }, false));

watch(sortFontsBy, () => {
    void getFonts();
});

async function getFonts() {
    if (!userHasScope.value) {
        return;
    }

    isLoading.value = true;
    const { data } = await apolloClient!.query<{ fonts: Font[] }>({
        fetchPolicy: "network-only",
        query: fontsQuery,
        variables: {
            clientId: clientId.value,
            orderBy: sortFontsBy.value
        }
    });

    fonts.value = data.fonts;
    isLoading.value = false;
    isInitialLoad.value = false;
}

async function getClient() {
    if (!userHasScope.value) return;

    const { data } = await apolloClient!.query<{
        client: { _id: string; mastheadUploadConfig: MastheadUploadConfig };
    }>({
        fetchPolicy: "network-only",
        query: clientUploadConfigQuery,
        variables: {
            clientId: clientId.value
        }
    });
    client.value = data.client;
}

function toggleAddFontModalIsVisible() {
    addFontModalIsVisible.value = !addFontModalIsVisible.value;
}

onMounted(() => {
    void getClient();
    void getFonts();
});
</script>

<style lang="scss">
@use "@style/variables" as *;

.fonts {
    margin-top: 70px;
    &__action {
        &-items {
            display: flex;
            justify-content: flex-end;
            padding: 15px 0;
        }

        &-item {
            flex: 0 0;
            display: flex;
            align-items: center;
            margin-left: 20px;
        }
    }

    &__list--no-action-items {
        padding: 15px 0 0 0;
    }

    &__section-divider {
        border: none;
        border-top: $border-width-base solid $cmp-light-border-color;
        flex: 1;
    }

    &__section-empty-message {
        color: $cmp-light-font-color;
        font-size: $font-size-base;
        margin: (0 - $spacing-small) 0 $spacing;

        p {
            margin: 0 0 $spacing-small;

            &:first-child {
                font-weight: bold;
            }
        }
    }

    &__section-header {
        align-items: center;
        display: flex;
        margin: 0 0 $spacing;
        padding: $spacing 0 0;
    }

    &__section-title {
        font-size: $font-size-large;
        font-weight: bold;
        padding: 0 $spacing-small 0 0;
    }

    .fonts__action-items {
        align-items: center;
        top: 44px;
        width: auto;
        position: fixed;
        z-index: 3;
        right: 28px;
        .data-filter__select {
            line-height: 30px;
        }
        button {
            &.fonts__action-item {
                padding: 6px 20px;
                height: 30px;
                line-height: 32px;
            }
        }
    }
}

.fonts-dashboard__empty-message-wrapper {
    padding: $spacing-large 0 0 0;

    button {
        margin: $spacing-small 0;
    }
}

.fonts-dashboard__loading-wrapper {
    height: 120px;
    position: relative;
}
</style>
